import {
  CRUDInputElementConstants,
  ECRUDFormLayoutType,
  EDatasourceType,
  EFormFieldSource,
  EFormSource,
  IActionDefinitionDto,
  ICRUDFormDiMetadata,
  ICRUDFormField,
  ICRUDGetFormDiCustomRequest,
  IDatabaseTableModel,
  IDatasourceDto,
  IDisplayItem,
  IFormSection,
  ILinkedTable
} from "@kuika/kuika-cl-model";
import classNames from "classnames";
import { Guid } from "guid-typescript";
import _ from "lodash";
import React from "react";
import { DragDropContext, DropResult, Droppable, OnDragEndResponder } from "react-beautiful-dnd";
import reactFastCompare from "react-fast-compare";
import { DatasourceService } from "../../../services/datasource-service";
import { FormService } from "../../../services/form-service";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { getFormElementTypeOptions } from "../../../shared/utility/form-element-type-options";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { ScreenDesignHelper } from "../../../shared/utility/screen-design-helper";
import ThreeDotSvg from "../../../svgs/small-icons/three-dot.svg";
import { KModalFooter } from "../../ui-kit/k-modal/k-modal-footer";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaCheckbox from "../../ui-kit/kuika-checkbox/kuika-checkbox";
import KuikaDropdownMenu from "../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import { DefaultModal } from "../default-modal";
import { FormSection, IElementTypeOption } from "./form-section";

export interface ICustomSourceOptions {
  tableName?: string;
}

interface IFormWizardCustomPropsDefault {
  mode: "default";
  formStatus: "draft" | "update";
  customSourceOptions: ICustomSourceOptions | null;
  /*
   * Form metadata to be edited.
   */
  metadata?: ICRUDFormDiMetadata;
  closeModal: () => void;
  setFooter: (footer: React.ReactNode) => void;
  /** Closes the wizard. Absent in `embed` mode, where the host owns the chrome. */
  onCancel?: () => void;
}

interface IFormWizardCustomPropsEmbedded {
  mode: "embed";
  formStatus: "draft" | "update";
  metadata: ICRUDFormDiMetadata;
  onMetadataChange: (metadata: ICRUDFormDiMetadata) => void;
}

type IFormWizardCustomProps = IFormWizardCustomPropsDefault | IFormWizardCustomPropsEmbedded;

interface IFormWizardCustomState {
  isUnlinkWarningModalOpen: boolean;
  isCreateLoading: boolean;
  sections: IFormSection[];
  isEmptySectionWarningModalVisible: boolean;
  linkedTable?: ILinkedTable;
  isTablesLoading: boolean;
  isSyncLoading: boolean;
  isUnlinkLoading: boolean;
  tables: IDatabaseTableModel[];
  syncedTable: IDatabaseTableModel | null;
  syncedAction: IActionDefinitionDto | null;
  syncedWorkflowStep: {
    processName: string;
    stepName: string;
  } | null;
  // diffArray: ICRUDFormFieldsDiff[];
  lastSyncedMetadata?: ICRUDFormDiMetadata;
  sectionsWithHeaderEditingInProgress: string[];
  idsOfFieldsWithNewColumnActive: string[];
  // For to be used with custom source with also create table option
  idsOfFieldsWhichFieldNamesAreTouched: string[];
}

class FormWizardCustom extends React.Component<IFormWizardCustomProps & IWithSDProps, IFormWizardCustomState> {
  constructor(props: IFormWizardCustomProps & IWithSDProps) {
    super(props);
    this.state = {
      isUnlinkWarningModalOpen: false,
      isTablesLoading: false,
      isSyncLoading: false,
      isUnlinkLoading: false,
      tables: [],
      isCreateLoading: false,
      isEmptySectionWarningModalVisible: false,
      sections: [
        {
          id: Guid.create().toString(),
          title: null,
          items: [this.getEmptyField()],
          responsiveLayoutSizes: {
            desktop: ECRUDFormLayoutType.TwoColumn,
            tablet: ECRUDFormLayoutType.TwoColumn,
            mobile: ECRUDFormLayoutType.SingleColumn
          }
        }
      ],
      syncedTable: null,
      syncedAction: null,
      syncedWorkflowStep: null,
      sectionsWithHeaderEditingInProgress: [],
      idsOfFieldsWithNewColumnActive: [],
      idsOfFieldsWhichFieldNamesAreTouched: []
    };
  }

  async componentDidMount() {
    this.setFooter();

    if (this.props.metadata) {
      this.setState({
        sections: this.initializeSections(this.props.metadata.sections),
        linkedTable: this.props.metadata.linkedTable
      });
    }

    await this.migrateMetadataIfNecessary();
    await Promise.all([this.fetchTables(), this.syncMetadata()]);
  }

  componentDidUpdate(prevProps, prevState: IFormWizardCustomState) {
    const previousSectionIds = prevState.sections.map((section) => section.id);
    const currentSectionIds = this.state.sections.map((section) => section.id);

    if (!_.isEqual(previousSectionIds, currentSectionIds)) {
      this.setState({
        sectionsWithHeaderEditingInProgress: this.state.sectionsWithHeaderEditingInProgress.filter((id) =>
          currentSectionIds.includes(id)
        )
      });
    }

    if (!reactFastCompare(prevState, this.state)) {
      this.setFooter();
    }
  }

  private setFooter = () => {
    if (this.props.mode === "embed") {
      return;
    }

    this.props.setFooter(
      <KModalFooter
        submitLabel="Save"
        submitTestId="createFormSaveButton"
        onSubmit={this.onClickCreate}
        onCancel={this.props.onCancel}
        loading={this.state.isCreateLoading}
      >
        <span className="form-wizard-footer__description">
          Create a form by adding sections and fields. Drag and drop fields between sections and change the layout of
          the sections for different screen sizes.
        </span>
      </KModalFooter>
    );
  };

  private setSectionsHeaderEditingInProgressValue = (sectionId: string, isEditingInProgress: boolean) => {
    if (isEditingInProgress) {
      this.setState({
        sectionsWithHeaderEditingInProgress: [...this.state.sectionsWithHeaderEditingInProgress, sectionId]
      });
    } else {
      this.setState({
        sectionsWithHeaderEditingInProgress: this.state.sectionsWithHeaderEditingInProgress.filter(
          (id) => id !== sectionId
        )
      });
    }
  };

  private setFieldsTouchedValue = (fieldId: string, isTouched: boolean) => {
    if (isTouched) {
      this.setState({
        idsOfFieldsWhichFieldNamesAreTouched: [...this.state.idsOfFieldsWhichFieldNamesAreTouched, fieldId]
      });
    } else {
      this.setState({
        idsOfFieldsWhichFieldNamesAreTouched: this.state.idsOfFieldsWhichFieldNamesAreTouched.filter(
          (id) => id !== fieldId
        )
      });
    }
  };

  private setFieldsNewColumnActiveValue = (fieldId: string, isNewColumnActive: boolean) => {
    if (isNewColumnActive) {
      this.setState({
        idsOfFieldsWithNewColumnActive: [...this.state.idsOfFieldsWithNewColumnActive, fieldId]
      });
    } else {
      this.setState({
        idsOfFieldsWithNewColumnActive: this.state.idsOfFieldsWithNewColumnActive.filter((id) => id !== fieldId)
      });
    }
  };

  private migrateMetadataIfNecessary = async () => {
    if (!this.isEditMode || !this.props.metadata) return;
    if (this.props.metadata.version >= 3) return;

    try {
      this.setState({ isSyncLoading: true });

      const response = await FormService.MigrateFormMetadata({
        metadata: this.props.metadata
      });

      if (response.status === 200 && response.data) {
        this.props.sd.setMetadataForFormWizard(response.data);
        this.setState({
          sections: this.initializeSections(response.data.sections),
          lastSyncedMetadata: response.data
        });
      }
    } catch (e) {
      KMainFunctions.exceptionHandler(e);
    } finally {
      this.setState({ isSyncLoading: false });
    }
  };

  private syncMetadata = async () => {
    if (
      this.isEditMode &&
      this.props.metadata?.source === EFormSource.DataTable &&
      this.props.metadata?.linkedTable?.tableName &&
      this.props.metadata?.linkedTable.tableName !== ""
    ) {
      await this.syncFormWithTable();
    }

    if (
      this.isEditMode &&
      this.props.metadata?.source === EFormSource.WorkflowBo &&
      this.props.metadata.linkedWorkflowBo?.processId &&
      this.props.metadata.linkedWorkflowBo?.processId !== "" &&
      this.props.metadata.linkedWorkflowBo.stepId &&
      this.props.metadata.linkedWorkflowBo.stepId !== ""
    ) {
      await this.syncFormWithWorkflowBo();
    }

    if (this.isEditMode && this.props.metadata?.source === EFormSource.Action) {
      const action = this.getSyncedAction();

      if (action) {
        await this.syncFormWithAction();
        this.setState({ syncedAction: action });
      }
    }
  };

  private syncFormWithAction = async () => {
    if (!this.isEditMode || !this.props.metadata) return;

    try {
      this.setState({ isSyncLoading: true });
      const response = await FormService.SyncFormMetadataWithAction(this.props.metadata);

      const syncedMetadata: ICRUDFormDiMetadata | undefined = response.data.metadata;

      if (response.status === 200 && syncedMetadata) {
        this.setState({
          sections: this.initializeSections(syncedMetadata.sections),
          lastSyncedMetadata: syncedMetadata
        });
      }
    } catch (e) {
      KMainFunctions.exceptionHandler(e);
    } finally {
      this.setState({ isSyncLoading: false });
    }
  };

  private initializeSections = (sections: IFormSection[]): IFormSection[] => {
    const _sections: IFormSection[] = _.cloneDeep(sections);

    _sections.forEach((section: IFormSection) => {
      section.items = this.initializeFields(section.items);
    });

    if (this.props.metadata?.source === EFormSource.WorkflowBo) {
      this.applyWorkflowRecommendedElementTypes(_sections);
    }

    return _sections;
  };

  private initializeFields = (fields: ICRUDFormField[]): ICRUDFormField[] => {
    const _fields: ICRUDFormField[] = _.cloneDeep(fields);

    fields.forEach((field: ICRUDFormField) => {
      try {
        // Default selection: if backend doesn't specify, auto-enable the field
        if (field.isEnabled === undefined || field.isEnabled === null) {
          field.isEnabled = true;
        }

        if (field.typeName.toLowerCase() === "datetimeoffset") {
          field.typeName = "DatetimeOffset";
        }

        if (field.typeName.toLowerCase() === "datetime") {
          field.typeName = "Datetime";
        }

        if (!field.elementType || field.elementType === "" || field.elementType === CRUDInputElementConstants.Auto) {
          field.elementType = this.getElementTypeOptions(field.typeName)[0].name;
        }

        // Migration: If field is checkbox, toggle switch, or Boolean type, ensure isRequired is false
        if (
          field.elementType === CRUDInputElementConstants.Checkbox ||
          field.elementType === CRUDInputElementConstants.ToggleSwitch ||
          field.typeName === "Boolean"
        ) {
          field.isRequired = false;
        }
      } catch (e) {
        field.elementType = CRUDInputElementConstants.Auto;
        KMainFunctions.displayWarningNotification(
          `Field ${field.name}'s element type has been reset because there has been an error while initializing it.`
        );
      } finally {
        const index: number = _fields.findIndex((f: ICRUDFormField) => f.id === field.id);
        _fields[index] = field;
      }
    });

    return _fields;
  };

  private applyWorkflowRecommendedElementTypes = (sections: IFormSection[]) => {
    try {
      const dict:
        | Record<string, { paramName: string; paramElement: string; isRequired?: boolean; isReadonly?: boolean }[]>
        | undefined = (window as any)?.kuikaWorkflowParamElements;
      const processId = this.props.metadata?.linkedWorkflowBo?.processId;
      const stepId = this.props.metadata?.linkedWorkflowBo?.stepId;
      if (!dict || !processId || !stepId) return;

      const process = this.props.sd.workFlowList.find((wf) => wf.id === processId);
      const processAny: any = process;
      const step = processAny?.nodes?.steps?.find((s: any) => s.id === stepId);
      const screenName = step?.name;
      if (!screenName) return;

      const entries = dict[screenName] || [];
      if (!entries.length) return;
      const map = new Map<string, { paramElement: string; isRequired?: boolean; isReadonly?: boolean }>(
        entries.map((e) => [
          // Normalize: compare without spaces for robustness
          e.paramName?.replace(/\s/g, "").toLowerCase(),
          { paramElement: e.paramElement, isRequired: e.isRequired, isReadonly: e.isReadonly }
        ])
      );

      sections.forEach((section) => {
        section.items.forEach((item) => {
          if (item.source === EFormFieldSource.WorkflowBusinessObjectParameter && item.linkedWorkflowBoParameterName) {
            const key = item.linkedWorkflowBoParameterName.replace(/\s/g, "").toLowerCase();
            const elementInfo = map.get(key);
            if (!elementInfo) return;
            const options = this.getElementTypeOptions(item.typeName);
            if (options.some((opt) => opt.name === elementInfo.paramElement)) {
              item.elementType = elementInfo.paramElement;
              // Apply isRequired and isReadonly if they exist
              if (elementInfo.isRequired !== undefined) {
                item.isRequired = elementInfo.isRequired;
              }
              if (elementInfo.isReadonly !== undefined) {
                item.isReadonlyField = elementInfo.isReadonly;
              }
              // Auto-select suggested parameters
              item.isEnabled = true;
            }
          }
        });
      });
    } catch {}
  };

  private get isCreateMode(): boolean {
    if (this.props.formStatus === "draft") {
      return true;
    }

    return this.props.metadata === undefined || this.props.metadata === null;
  }

  private get isEditMode(): boolean {
    return !this.isCreateMode;
  }

  private getSyncedAction = () => {
    try {
      const action: IActionDefinitionDto | undefined = this.props.sd.actionDefinitions.find(
        (ad) => ad.id === this.props.metadata?.linkedAction?.actionDefinitionId
      );

      return action;
    } catch (e) {
      KMainFunctions.exceptionHandler(e);
    }
  };

  private fetchTables = async () => {
    const managedDbDataSource: IDatasourceDto | undefined = this.props.sd.datasources.find(
      (ds: IDatasourceDto) => ds.datasourceType === EDatasourceType.ManagedDb
    );

    if (!managedDbDataSource) {
      return;
    }

    try {
      this.setState({ isTablesLoading: true });
      const response = await DatasourceService.GetSchema(managedDbDataSource.id, true);

      // TODO: Code smell: Backend'e tasinmasi lazim. screen-design-add'de de var.
      const defaultKuikaTableNames: string[] = KMainFunctions.getDefaultKuikaTableNames();

      if (response.status === 200) {
        const allowedKuikaTables = ["KUser"];
        const tables: IDatabaseTableModel[] = response.data.tables.filter(
          (t: IDatabaseTableModel) => !defaultKuikaTableNames.includes(t.name) || allowedKuikaTables.includes(t.name)
        );

        let syncedTable: IDatabaseTableModel | null =
          response.data.tables.find(
            (t: IDatabaseTableModel) => `${t.schema}.${t.name}` === this.props.metadata?.linkedTable?.tableName
          ) || null;

        // Migration: if linkedTable.tableName is in old format (without schema), find and update
        if (!syncedTable && this.props.metadata?.linkedTable?.tableName) {
          let tableNameToMatch = this.props.metadata.linkedTable.tableName;

          if (tableNameToMatch.includes(".")) {
            const parts = tableNameToMatch.split(".");
            tableNameToMatch = parts[parts.length - 1];
          }

          syncedTable = response.data.tables.find((t: IDatabaseTableModel) => t.name === tableNameToMatch) || null;

          if (syncedTable) {
            this.props.sd.setMetadataForFormWizard({
              ...this.props.metadata,
              linkedTable: {
                ...this.props.metadata.linkedTable,
                tableName: syncedTable.name
              }
            });
          }
        }

        this.setState({
          tables,
          syncedTable
        });
      }
    } catch (e) {
      KMainFunctions.exceptionHandler(e);
    } finally {
      this.setState({ isTablesLoading: false });
    }
  };

  private onClickInverseSelection = () => {
    const sections: IFormSection[] = [...this.state.sections];
    sections.forEach((section: IFormSection) => {
      section.items.forEach((item: ICRUDFormField) => {
        item.isEnabled = !item.isEnabled;
      });
    });

    this.setState({ sections });
  };

  private updateSection = (sectionIndex: number, section: IFormSection) => {
    if (this.getIsSyncedTableNotFound()) {
      KMainFunctions.displayWarningNotification("You can not edit the form until form is unlinked from the table.");
      return;
    }

    const sections: IFormSection[] = _.cloneDeep(this.state.sections);
    sections[sectionIndex] = section;
    this.setState({ sections });

    if (this.props.mode === "embed") {
      this.props.onMetadataChange({
        ...this.props.metadata,
        sections
      });
    }
  };

  private getSelectionsCheckboxValue = (): boolean => {
    const items: ICRUDFormField[] = this.state.sections.map((section: IFormSection) => section.items).flat();
    const enabledItems: ICRUDFormField[] = items.filter((item: ICRUDFormField) => item.isEnabled);
    return enabledItems.length === items.length;
  };

  private getSelectionsCheckboxIsIndeterminate = (): boolean => {
    const items: ICRUDFormField[] = this.state.sections.map((section: IFormSection) => section.items).flat();
    const enabledItems: ICRUDFormField[] = items.filter((item: ICRUDFormField) => item.isEnabled);
    return enabledItems.length > 0 && enabledItems.length < items.length;
  };

  private onChangeSelectionsCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sections: IFormSection[] = _.cloneDeep(this.state.sections);
    sections.forEach((section: IFormSection) => {
      section.items.forEach((item: ICRUDFormField) => {
        item.isEnabled = e.target.checked;
      });
    });
    this.setState({ sections });
  };

  private onDragEnd: OnDragEndResponder = (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    const sourceSectionIndex: number = parseInt(result.source.droppableId.replace("droppable-section-", ""), 10);
    let sourceSection: IFormSection = this.state.sections[sourceSectionIndex];

    if (!sourceSection) {
      return;
    }

    if (result.destination.droppableId === "new-section") {
      sourceSection = _.cloneDeep(sourceSection);
      const formItemIndex: number = result.source.index;
      const formItem: ICRUDFormField = sourceSection.items[formItemIndex];
      sourceSection.items.splice(formItemIndex, 1);
      const sections: IFormSection[] = [...this.state.sections];
      sections[sourceSectionIndex] = sourceSection;

      if (sourceSection.items.length === 0) {
        sections.splice(sourceSectionIndex, 1);
        KMainFunctions.displayWarningNotification("Section removed because it has no items.");
      }

      const newSection: IFormSection = {
        id: Guid.create().toString(),
        title: null,
        items: [formItem],
        responsiveLayoutSizes: {
          desktop: ECRUDFormLayoutType.TwoColumn,
          tablet: ECRUDFormLayoutType.TwoColumn,
          mobile: ECRUDFormLayoutType.SingleColumn
        }
      };

      sections.push(newSection);

      this.setState({ sections });
      return;
    }

    if (result.source.droppableId === result.destination.droppableId) {
      const items: ICRUDFormField[] = this.reorder(sourceSection.items, result.source.index, result.destination.index);
      this.updateSection(sourceSectionIndex, { ...sourceSection, items });
    }

    if (result.source.droppableId !== result.destination.droppableId) {
      const destinationSectionIndex: number = parseInt(
        result.destination.droppableId.replace("droppable-section-", ""),
        10
      );
      const destinationSection: IFormSection = this.state.sections[destinationSectionIndex];
      const sourceFormItemIndex: number = result.source.index;
      const sourceFormItem: ICRUDFormField = sourceSection.items[sourceFormItemIndex];
      sourceSection = _.cloneDeep(sourceSection);
      sourceSection.items.splice(sourceFormItemIndex, 1);
      const destinationFormItemIndex: number = result.destination.index;
      destinationSection.items.splice(destinationFormItemIndex, 0, sourceFormItem);
      const sections: IFormSection[] = [...this.state.sections];
      sections[sourceSectionIndex] = sourceSection;
      sections[destinationSectionIndex] = destinationSection;

      if (sourceSection.items.length === 0) {
        sections.splice(sourceSectionIndex, 1);
        KMainFunctions.displayWarningNotification("Section removed because it has no items.");
      }

      this.setState({ sections });
    }
  };

  reorder = (list: ICRUDFormField[], startIndex: number, endIndex: number): ICRUDFormField[] => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  onClickAddItem = (sectionIndex: number) => {
    if (this.getIsSyncedTableNotFound()) {
      this.displayUnlinkWarning();
      return;
    }

    const section: IFormSection = _.cloneDeep(this.state.sections[sectionIndex]);
    const items: ICRUDFormField[] = [...section.items];
    const newItem: ICRUDFormField = this.getEmptyField();
    items.push(newItem);
    section.items = items;
    const sections: IFormSection[] = [...this.state.sections];
    sections[sectionIndex] = section;
    this.setState({ sections });
  };

  onClickDeleteItem = (sectionIndex: number, itemIndex: number) => {
    if (this.getIsSyncedTableNotFound()) {
      this.displayUnlinkWarning();
      return;
    }

    if (this.state.sections.length === 1 && this.state.sections[sectionIndex].items.length === 1) {
      KMainFunctions.displayWarningNotification("You can't delete the last item of the last section.");
      return;
    }

    if (
      this.state.sections[sectionIndex].items[itemIndex].source === EFormFieldSource.WorkflowBusinessObjectParameter
    ) {
      KMainFunctions.displayWarningNotification(
        "Business object fields can not be deleted. Either disable the field or make the field readonly."
      );
      return;
    }

    const section: IFormSection = _.cloneDeep(this.state.sections[sectionIndex]);
    const items: ICRUDFormField[] = [...section.items];
    items.splice(itemIndex, 1);
    section.items = items;
    const sections: IFormSection[] = [...this.state.sections];
    sections[sectionIndex] = section;

    if (section.items.length === 0) {
      sections.splice(sectionIndex, 1);
      KMainFunctions.displayWarningNotification("Section removed because it has no items.");
    }

    this.setState({ sections });
  };

  private displayUnlinkWarning = () => {
    KMainFunctions.displayWarningNotification("You can not edit the form until form is unlinked from the table.");
  };

  private getEmptyField = (): ICRUDFormField => {
    return FormWizardCustom.getEmptyField({
      source:
        this.props.mode === "default" && this.props.customSourceOptions?.tableName
          ? EFormFieldSource.TableColumn
          : EFormFieldSource.Custom
    });
  };

  private static getEmptyField = (partial: Partial<ICRUDFormField> = {}): ICRUDFormField => {
    return {
      id: Guid.create().toString(),
      isEnabled: true,
      typeName: "",
      name: "",
      isRequired: false,
      isReadonlyField: false,
      elementType: "",
      decimalScale: null,
      source: EFormFieldSource.Custom,
      ...partial
    };
  };

  onClickAddSection = (items: ICRUDFormField[] = [this.getEmptyField()]) => {
    if (this.getIsSyncedTableNotFound()) {
      this.displayUnlinkWarning();
      return;
    }

    const sections: IFormSection[] = [...this.state.sections];
    const newSection: IFormSection = {
      id: Guid.create().toString(),
      title: null,
      responsiveLayoutSizes: {
        desktop: ECRUDFormLayoutType.TwoColumn,
        tablet: ECRUDFormLayoutType.TwoColumn,
        mobile: ECRUDFormLayoutType.SingleColumn
      },
      items
    };

    sections.push(newSection);

    this.setState({ sections });
  };

  private createFormWizardCustom = async () => {
    try {
      if (!this.props.sd.targetKuikaIdForFormWizard) {
        return;
      }

      this.setState({ isCreateLoading: true });

      const sections: IFormSection[] = FormWizardCustom.trimAllFieldNames(this.state.sections);
      const fieldsWithNewColumnActive = this.state.sections
        .map((section) => section.items)
        .flat()
        .filter(
          (field) =>
            field.source === EFormFieldSource.TableColumn &&
            this.state.idsOfFieldsWithNewColumnActive.includes(field.id)
        );

      const fieldsWithInvalidCharacters =
        FormWizardCustom.getFieldNamesWithInvalidCharacters(fieldsWithNewColumnActive);

      if (this.getFieldsWithNonUniqueLinkedColumnNames().length > 0) {
        const fieldsWithNonUniqueLinkedColumnNames: string[] = this.getFieldsWithNonUniqueLinkedColumnNames();
        const invalidFieldNames: string = fieldsWithNonUniqueLinkedColumnNames.join(", ");

        KMainFunctions.displayWarningNotification(
          `Field names can not be the same with other field names. Invalid field names: ${invalidFieldNames}`
        );
        return;
      }

      if (fieldsWithInvalidCharacters.containsNonLatinCharacters.length > 0) {
        const invalidFieldNames: string = fieldsWithInvalidCharacters.containsNonLatinCharacters.join(", ");
        KMainFunctions.displayWarningNotification(
          `Field names can not contain non-latin characters. Invalid field names: ${invalidFieldNames}`
        );
        return;
      }

      if (fieldsWithInvalidCharacters.containsSpecialCharacters.length > 0) {
        KMainFunctions.displayWarningNotification(
          `Field names can not contain special characters. Invalid field names: ${fieldsWithInvalidCharacters.containsSpecialCharacters.join(
            ", "
          )}`
        );
        return;
      }

      if (fieldsWithInvalidCharacters.containsSpace.length > 0) {
        KMainFunctions.displayWarningNotification(
          `Field names can not contain space. Invalid field names: ${fieldsWithInvalidCharacters.containsSpace.join(
            ", "
          )}`
        );
        return;
      }

      let currentDiSnapshot: string | undefined;

      if (this.props.metadata?.predefinedFormDiId) {
        const currentDi: IDisplayItem | undefined =
          this.props.sd.screenDesignDictionary[this.props.metadata.predefinedFormDiId];
        if (currentDi) {
          currentDiSnapshot = JSON.stringify(currentDi);
        }
      }

      const request: ICRUDGetFormDiCustomRequest = {
        ...this.props.metadata,
        version: 3,
        source: this.props.metadata?.source ?? EFormSource.Custom,
        projectId: this.props.sd.project.id,
        predefinedFormDiId: this.props.metadata?.predefinedFormDiId,
        predefinedFormFooterIds: this.props.metadata?.predefinedFormFooterIds,
        currentDiSnapshot,
        sections
      };

      if (this.props.mode === "default" && !this.props.metadata && this.props.customSourceOptions?.tableName) {
        let tableName = this.props.customSourceOptions.tableName;

        if (tableName.includes(".")) {
          const parts = tableName.split(".");
          tableName = parts[parts.length - 1];
        }

        request.linkedTable = {
          tableName
        };
      }

      if (
        this.props.metadata?.linkedTable?.tableName &&
        this.props.metadata.linkedTable.tableName.length > 0 &&
        this.props.metadata.linkedTable?.tableId
      ) {
        let tableName = this.props.metadata.linkedTable.tableName;

        if (tableName.includes(".")) {
          const parts = tableName.split(".");
          tableName = parts[parts.length - 1];
        }

        request.linkedTable = {
          ...this.props.metadata.linkedTable,
          tableName
        };
      }

      const response = await FormService.GetFormDiCustom(request);

      if (response.status === 200) {
        const displayItem: IDisplayItem = response.data;

        const formDi: IDisplayItem | undefined = ScreenDesignHelper.findDisplayItemByComponentName("Form", [
          response.data
        ]);

        if (!this.isCreateMode) {
          this.props.sd.replaceDi({
            displayItem,
            targetKuikaId: this.props.sd.targetKuikaIdForFormWizard.toString()
          });
        } else {
          this.props.sd.injectDiToTargetDi({
            displayItem,
            targetKuikaId: this.props.sd.targetKuikaIdForFormWizard.toString()
          });
        }

        if (formDi) {
          setTimeout(() => {
            this.props.sd.setSelectedDisplayItem(formDi.id.toString());
          }, 200);
        }

        if (this.props.mode === "default") {
          this.props.closeModal();
        }
      }
    } catch (e) {
      KMainFunctions.exceptionHandler(e);
    } finally {
      this.setState({ isCreateLoading: false });
    }
  };

  private getFieldsWithNonUniqueLinkedColumnNames = (): string[] => {
    const allFieldNames: string[] = this.state.sections
      .map((section: IFormSection) => section.items)
      .filter((items: ICRUDFormField[]) => items.some((item: ICRUDFormField) => item.isEnabled))
      .flat()
      .filter((item: ICRUDFormField) => item.linkedColumnName)
      .map((item: ICRUDFormField) => item.linkedColumnName!);
    const uniqueFieldNames: string[] = _.uniq(allFieldNames);

    if (allFieldNames.length === uniqueFieldNames.length) {
      return [];
    }

    return _.uniq(
      allFieldNames.filter(
        (fieldName: string) => allFieldNames.indexOf(fieldName) !== allFieldNames.lastIndexOf(fieldName)
      )
    );
  };

  private onClickCreate = async () => {
    const emptySections: IFormSection[] = this.getEmptySections();

    if (emptySections.length > 0) {
      this.setState({ isEmptySectionWarningModalVisible: true });
      return;
    }

    if (this.getIsSyncedTableNotFound()) {
      this.displayUnlinkWarning();
      return;
    }

    if (this.state.sectionsWithHeaderEditingInProgress.length > 0) {
      KMainFunctions.displayWarningNotification("Please finish renaming sections before saving the form.");
      return;
    }

    if (this.props.mode === "default") {
      await this.createFormWizardCustom();
    }
  };

  /**
   * KOA-17362 — the widget-per-type table now lives in the pure
   * `shared/utility/form-element-type-options` module so the DSL's
   * `SCREEN_FIELD … element:<Type>` validation uses the SAME option set as this
   * picker (two copies would drift). Behaviour is unchanged: table-typed fields
   * still offer only the Select variants, hence the table names passed through.
   */
  private getElementTypeOptions = (type: string): IElementTypeOption[] => {
    return getFormElementTypeOptions(
      type,
      this.state.tables.map((table: IDatabaseTableModel): string => `${table.schema}.${table.name}`)
    );
  };

  private syncFormWithWorkflowBo = async () => {
    if (!this.isEditMode || !this.props.metadata) return;

    try {
      this.setState({ isSyncLoading: true });
      const response = await FormService.SyncFormMetadataWithWorkflowBo(this.props.metadata);

      const syncedMetadata: ICRUDFormDiMetadata | undefined = response.data.metadata;

      if (response.status === 200 && syncedMetadata) {
        this.setState({
          sections: this.initializeSections(syncedMetadata.sections),
          lastSyncedMetadata: syncedMetadata,
          syncedWorkflowStep: {
            processName: response.data.processName,
            stepName: response.data.stepName
          }
        });
      }
    } catch (e) {
      KMainFunctions.exceptionHandler(e);
    } finally {
      this.setState({ isSyncLoading: false });
    }
  };

  private syncFormWithTable = async () => {
    if (!this.isEditMode || !this.props.metadata) return;

    try {
      this.setState({ isSyncLoading: true });
      const response = await FormService.SyncFormMetadataWithTable(this.props.metadata);

      const syncedMetadata: ICRUDFormDiMetadata | undefined = response.data.metadata;

      if (response.status === 200 && syncedMetadata) {
        this.setState({
          sections: this.initializeSections(syncedMetadata.sections),
          lastSyncedMetadata: syncedMetadata
        });

        // Difference between this.props.metadata and syncedMetadata

        // const diffArray: ICRUDFormFieldsDiff[] = [];
        //
        // const fields: ICRUDFormField[] = this.props.metadata.sections
        //   .map((section: IFormSection) => section.items.map((item: ICRUDFormField): ICRUDFormField => item))
        //   .flat();
        //
        // const syncedFields: ICRUDFormField[] = syncedMetadata.sections
        //   .map((section: IFormSection) => section.items.map((item: ICRUDFormField): ICRUDFormField => item))
        //   .flat();
        //
        // const removedFields: ICRUDFormField[] = fields.filter(
        //   (f: ICRUDFormField) => !syncedFields.some((sf: ICRUDFormField) => sf.id === f.id)
        // );
        //
        // removedFields.forEach((removedField: ICRUDFormField) => {
        //   diffArray.push({
        //     previous: removedField,
        //     current: null
        //   });
        // });
        //
        // const addedFields: ICRUDFormField[] = syncedFields.filter(
        //   (sf: ICRUDFormField) => !fields.some((f: ICRUDFormField) => f.id === sf.id)
        // );
        //
        // addedFields.forEach((addedField: ICRUDFormField) => {
        //   if (addedField.elementType === "auto") {
        //     addedField.elementType = this.getElementTypeOptions(addedField.typeName)[0].name;
        //   }
        //
        //   diffArray.push({
        //     previous: null,
        //     current: addedField
        //   });
        // });
        //
        // // Not in removed fields; not in added fields.
        // const possiblyUpdatedFields: ICRUDFormField[] = syncedFields
        //   .filter((sf: ICRUDFormField) => !addedFields.some((af: ICRUDFormField) => af.id === sf.id))
        //   .filter((sf: ICRUDFormField) => !removedFields.some((af: ICRUDFormField) => af.id === sf.id));
        //
        // const unchangedPossiblyUpdatedFields: ICRUDFormField[] = fields.filter((f) =>
        //   possiblyUpdatedFields.some((puf) => puf.id === f.id)
        // );
        //
        // for (const field of unchangedPossiblyUpdatedFields) {
        //   const possiblyUpdatedField: ICRUDFormField | undefined = possiblyUpdatedFields.find(
        //     (puf: ICRUDFormField): boolean => puf.id === field.id
        //   );
        //
        //   if (!possiblyUpdatedField) {
        //     continue;
        //   }
        //
        //   // Idk why this is necessary but it is.
        //   if (!possiblyUpdatedField?.isRequired) {
        //     possiblyUpdatedField.isRequired = false;
        //   }
        //
        //   // Idk why this is necessary but it is.
        //   if (!possiblyUpdatedField?.decimalScale) {
        //     possiblyUpdatedField.decimalScale = null;
        //   }
        //
        //   if (!equal(field, possiblyUpdatedField)) {
        //     diffArray.push({
        //       previous: field,
        //       current: possiblyUpdatedField
        //     });
        //   }
        // }
        //
        // this.setState({ diffArray });
      }
    } catch (e) {
      KMainFunctions.exceptionHandler(e);
    } finally {
      this.setState({ isSyncLoading: false });
    }
  };

  private getIsFormFieldValid = (formField: ICRUDFormField): boolean => {
    if (_.isEmpty(formField.typeName)) {
      return false;
    }

    if (_.isEmpty(formField.name.trim())) {
      return false;
    }

    if (formField.source === EFormFieldSource.TableColumn && _.isEmpty(formField.linkedColumnName)) {
      return false;
    }

    if (_.isEmpty(formField.elementType)) {
      return false;
    }

    return true;
  };

  private getIsCreateButtonDisabled = (): boolean => {
    if (this.state.isTablesLoading || this.state.isSyncLoading) return true;
    if (this.getIsNextButtonLoading()) return true;
    if (this.getIsSyncedTableNotFound()) return true;
    if (!this.state.sections) return true;
    if (this.state.sections.length === 0) return true;

    // Disable create button if all form fields are disabled
    if (
      this.state.sections.every((section: IFormSection) =>
        section.items.every((item: ICRUDFormField) => !item.isEnabled)
      )
    ) {
      return true;
    }

    // Disable create button if any of the enabled form fields is invalid
    if (
      this.state.sections.some((section: IFormSection) =>
        section.items.some((item: ICRUDFormField) => item.isEnabled && !this.getIsFormFieldValid(item))
      )
    ) {
      return true;
    }

    return false;
  };

  private getEmptySections = (): IFormSection[] => {
    return this.state.sections.filter(
      (section: IFormSection): boolean => section.items.filter((item: ICRUDFormField) => item.isEnabled).length === 0
    );
  };

  private getSectionTitle = (section: IFormSection): string => {
    if (!section.title) {
      const sectionIndex: number = this.state.sections.findIndex((s: IFormSection) => s.id === section.id);
      return `Section ${sectionIndex + 1}`;
    }

    return section.title;
  };

  private onClickContinueOnEmptySectionWarningModal = async () => {
    this.setState({ isEmptySectionWarningModalVisible: false });
    await this.createFormWizardCustom();
  };

  private onClickCancelOnEmptySectionWarningModal = () => {
    this.setState({ isEmptySectionWarningModalVisible: false });
  };

  private static trimAllFieldNames = (sections: IFormSection[]): IFormSection[] => {
    const newSections: IFormSection[] = _.cloneDeep(sections);

    newSections.forEach((section: IFormSection) => {
      section.items.forEach((item: ICRUDFormField) => {
        item.name = item.name.trim();
      });
    });

    return newSections;
  };

  private static getFieldNamesWithInvalidCharacters = (fields: ICRUDFormField[]) => {
    const containsNonLatinCharacters: string[] = [];
    const containsSpecialCharacters: string[] = [];
    const containsSpace: string[] = [];

    fields.forEach((item: ICRUDFormField) => {
      if (item.linkedColumnName && KMainFunctions.getIsStringContainsNonLatinCharacter(item.linkedColumnName)) {
        containsNonLatinCharacters.push(item.linkedColumnName);
      }

      if (item.linkedColumnName && KMainFunctions.getIsStringContainsSpecialCharacter(item.linkedColumnName)) {
        containsSpecialCharacters.push(item.linkedColumnName);
      }

      if (item.linkedColumnName && item.linkedColumnName.includes(" ")) {
        containsSpace.push(item.linkedColumnName);
      }
    });

    return {
      containsNonLatinCharacters,
      containsSpecialCharacters,
      containsSpace
    };
  };

  private getIsSyncedTableNotFound = (): boolean => {
    return (
      this.isEditMode &&
      !this.state.isTablesLoading &&
      !this.state.isSyncLoading &&
      Boolean(this.props.metadata?.linkedTable?.tableName) &&
      this.props.metadata?.linkedTable?.tableName !== "" &&
      !this.state.syncedTable
    );
  };

  private getIsNextButtonLoading = (): boolean => {
    return this.state.isCreateLoading || this.state.isTablesLoading || this.state.isSyncLoading;
  };

  private onClickUnlinkDotDotDot = () => {
    this.setState({ isUnlinkWarningModalOpen: true });
  };

  private onClickUnlink = async () => {
    if (!this.props.metadata) return;

    try {
      this.setState({ isUnlinkLoading: true });
      const response = await FormService.UnlinkFormFromSource(this.props.metadata);

      if (response.status === 200 && response.data) {
        this.props.sd.setMetadataForFormWizard(response.data);
        this.setState({
          linkedTable: undefined,
          syncedTable: null,
          syncedWorkflowStep: null,
          tables: [],
          sections: response.data.sections
          // diffArray: []
        });

        KMainFunctions.displaySuccessNotification(
          "Form is successfully unlinked from the table. Click SAVE to apply the changes."
        );
      }
    } catch (e) {
      KMainFunctions.exceptionHandler(e);
    } finally {
      this.setState({ isUnlinkLoading: false, isUnlinkWarningModalOpen: false });
    }
  };

  private onClickCancelUnlinkWarningModal = () => {
    this.setState({ isUnlinkWarningModalOpen: false });
  };

  render() {
    return (
      <DragDropContext onDragEnd={this.onDragEnd}>
        <div className="form-wizard-inner-container">
          <div className="form-wizard-top-options">
            <div className="form-wizard-top-options__selection">
              <KuikaCheckbox
                checked={this.getSelectionsCheckboxValue()}
                indeterminate={this.getSelectionsCheckboxIsIndeterminate()}
                onChange={this.onChangeSelectionsCheckbox}
                dataTestId="createFormModalSelectAllFields"
              />
              <KuikaButton
                type="tertiary"
                size="sm"
                onClick={this.onClickInverseSelection}
                data-testid="createFormModalInverseSelection"
              >
                Inverse Selection
              </KuikaButton>
            </div>
            <div className="form-wizard-top-options__source" />

            {this.isEditMode && !this.state.syncedTable && (this.state.isTablesLoading || this.state.isSyncLoading) && (
              <div className="form-wizard-top-options__create-table">
                {/* TODO: Loading Icon */}
                <div className="form-wizard-top-options__create-table__loading">Syncing...</div>
              </div>
            )}

            {this.isEditMode && this.state.syncedTable && (
              <div className="form-wizard-top-options__create-table">
                <div>
                  <strong>Synced With</strong>: ManagedDB /{" "}
                  <span>{`${this.state.syncedTable.schema}.${this.state.syncedTable.name}`}</span>
                </div>
                <KuikaDropdownMenu
                  placement="bottomRight"
                  options={[{ key: "unlink-source", label: "Unlink", onClick: this.onClickUnlinkDotDotDot }]}
                >
                  <KuikaButton
                    type="tertiary"
                    color="neutral"
                    size="sm"
                    isIcon
                    aria-label="Source actions"
                    data-testid="createFormModalSourceActions"
                  >
                    <ThreeDotSvg />
                  </KuikaButton>
                </KuikaDropdownMenu>
              </div>
            )}

            {this.isEditMode &&
              this.props.metadata?.source === EFormSource.WorkflowBo &&
              this.state.syncedWorkflowStep && (
                <div className="form-wizard-top-options__create-table">
                  <div>
                    <strong>Synced With</strong>: <span>{this.state.syncedWorkflowStep.processName}</span>
                    <span>&nbsp;/&nbsp;</span>
                    <span>{this.state.syncedWorkflowStep.stepName}</span>
                  </div>
                </div>
              )}

            {this.isEditMode && this.props.metadata?.source === EFormSource.Action && this.state.syncedAction && (
              <div className="form-wizard-top-options__create-table">
                <div>
                  <strong>Synced With</strong>: <span>Action</span>
                  <span>&nbsp;/&nbsp;</span>
                  <span>{this.state.syncedAction.name}</span>
                </div>
                <KuikaDropdownMenu
                  placement="bottomRight"
                  options={[{ key: "unlink-source", label: "Unlink", onClick: this.onClickUnlinkDotDotDot }]}
                >
                  <KuikaButton
                    type="tertiary"
                    color="neutral"
                    size="sm"
                    isIcon
                    aria-label="Source actions"
                    data-testid="createFormModalSourceActions"
                  >
                    <ThreeDotSvg />
                  </KuikaButton>
                </KuikaDropdownMenu>
              </div>
            )}
            {this.getIsSyncedTableNotFound() && (
              <div className="form-wizard-top-options__create-table">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="var(--red-5)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="feather feather-alert-circle"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <span style={{ fontSize: 12 }}>
                  Synced Table Not Found: <strong>{this.props.metadata?.linkedTable?.tableName}</strong>.<br /> Unlink
                  to continue to edit the table.
                </span>
                <KuikaButton onClick={this.onClickUnlink}>Unlink</KuikaButton>
              </div>
            )}
          </div>
          <div className="form-wizard-sections">
            {this.state.sections.map((section: IFormSection, index: number) => (
              <FormSection
                key={`form-section-${section.id}-${index}`}
                metadata={this.props.metadata}
                sectionIndex={index}
                section={section}
                updateSection={this.updateSection}
                onClickAddItem={this.onClickAddItem}
                onClickDeleteItem={this.onClickDeleteItem}
                tables={this.state.tables}
                getElementTypeOptions={this.getElementTypeOptions}
                isEditMode={this.isEditMode}
                isCreateMode={this.isCreateMode}
                formSource={this.props.metadata?.source ?? EFormSource.Custom}
                customSourceOptions={
                  this.props.mode === "default" && this.props.customSourceOptions
                    ? this.props.customSourceOptions
                    : null
                }
                lastSyncedSectionSnapshot={this.state.lastSyncedMetadata?.sections.find(
                  (_section) => _section.id === section.id
                )}
                setSectionsHeaderEditingInProgressValue={(isEditingInProgress: boolean) =>
                  this.setSectionsHeaderEditingInProgressValue(section.id, isEditingInProgress)
                }
                setFieldsTouchedValue={this.setFieldsTouchedValue}
                setFieldsNewColumnActiveValue={this.setFieldsNewColumnActiveValue}
                idsOfFieldsWithNewColumnActive={this.state.idsOfFieldsWithNewColumnActive.filter((id) =>
                  section.items.some((item) => item.id === id)
                )}
                idsOfFieldsWhichFieldNamesAreTouched={this.state.idsOfFieldsWhichFieldNamesAreTouched.filter((id) =>
                  section.items.some((item) => item.id === id)
                )}
              />
            ))}
          </div>
          <Droppable droppableId="new-section">
            {(provided, snapshot) => (
              <div
                className={classNames({
                  "form-wizard-drop-area": true,
                  "form-wizard-drop-area--is-dragging": snapshot.isDraggingOver
                })}
                ref={provided.innerRef}
              >
                <div className="form-wizard-drop-area__text">Create a new section by drag and drop.</div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="48"
                  height="54"
                  viewBox="0 0 48 54"
                  fill="none"
                  style={{
                    position: "absolute",
                    top: 44,
                    left: 10
                  }}
                >
                  <path
                    d="M47.3536 46.3536C47.5488 46.1583 47.5488 45.8417 47.3536 45.6464L44.1716 42.4645C43.9763 42.2692 43.6597 42.2692 43.4645 42.4645C43.2692 42.6597 43.2692 42.9763 43.4645 43.1716L46.2929 46L43.4645 48.8284C43.2692 49.0237 43.2692 49.3403 43.4645 49.5355C43.6597 49.7308 43.9763 49.7308 44.1716 49.5355L47.3536 46.3536ZM0.641279 3.65141C0.662668 3.92673 0.903192 4.13257 1.1785 4.11118C1.45382 4.0898 1.65966 3.84927 1.63828 3.57396L0.641279 3.65141ZM2.74755 10.6268C2.68329 10.3582 2.41349 10.1926 2.14493 10.2569C1.87637 10.3211 1.71074 10.5909 1.775 10.8595L2.74755 10.6268ZM4.02864 17.7998C4.13442 18.0548 4.42695 18.1759 4.68203 18.0701C4.93711 17.9643 5.05814 17.6718 4.95237 17.4167L4.02864 17.7998ZM8.1987 23.7761C8.05419 23.5408 7.74629 23.4672 7.51098 23.6117C7.27566 23.7562 7.20204 24.0641 7.34655 24.2994L8.1987 23.7761ZM11.6404 30.1998C11.8199 30.4097 12.1355 30.4344 12.3454 30.255C12.5553 30.0756 12.58 29.7599 12.4005 29.55L11.6404 30.1998ZM17.4499 34.5995C17.24 34.42 16.9244 34.4447 16.745 34.6546C16.5656 34.8645 16.5902 35.1801 16.8001 35.3596L17.4499 34.5995ZM22.7006 39.6534C22.9359 39.798 23.2438 39.7243 23.3883 39.489C23.5328 39.2537 23.4592 38.9458 23.2239 38.8013L22.7006 39.6534ZM29.5833 42.0476C29.3282 41.9418 29.0357 42.0629 28.9299 42.318C28.8241 42.573 28.9451 42.8656 29.2002 42.9714L29.5833 42.0476ZM36.1405 45.225C36.4091 45.2893 36.6789 45.1236 36.7431 44.8551C36.8074 44.5865 36.6418 44.3167 36.3732 44.2524L36.1405 45.225ZM43.426 45.3617C43.1507 45.3403 42.9102 45.5462 42.8888 45.8215C42.8674 46.0968 43.0733 46.3373 43.3486 46.3587L43.426 45.3617ZM0.5 0C0.5 1.22876 0.547671 2.44648 0.641279 3.65141L1.63828 3.57396C1.54667 2.39477 1.5 1.20289 1.5 0H0.5ZM1.775 10.8595C2.34692 13.2499 3.10344 15.5687 4.02864 17.7998L4.95237 17.4167C4.0472 15.2339 3.30708 12.9654 2.74755 10.6268L1.775 10.8595ZM7.34655 24.2994C8.62434 26.3802 10.0613 28.3527 11.6404 30.1998L12.4005 29.55C10.8552 27.7424 9.44903 25.8122 8.1987 23.7761L7.34655 24.2994ZM16.8001 35.3596C18.6473 36.9387 20.6198 38.3756 22.7006 39.6534L23.2239 38.8013C21.1878 37.551 19.2576 36.1448 17.4499 34.5995L16.8001 35.3596ZM29.2002 42.9714C31.4313 43.8966 33.7501 44.6531 36.1405 45.225L36.3732 44.2524C34.0345 43.6929 31.7661 42.9528 29.5833 42.0476L29.2002 42.9714ZM43.3486 46.3587C44.5535 46.4523 45.7712 46.5 47 46.5V45.5C45.7971 45.5 44.6052 45.4533 43.426 45.3617L43.3486 46.3587ZM47.7071 46.7071C48.0976 46.3166 48.0976 45.6834 47.7071 45.2929L41.3431 38.9289C40.9526 38.5384 40.3195 38.5384 39.9289 38.9289C39.5384 39.3195 39.5384 39.9526 39.9289 40.3431L45.5858 46L39.9289 51.6569C39.5384 52.0474 39.5384 52.6805 39.9289 53.0711C40.3195 53.4616 40.9526 53.4616 41.3431 53.0711L47.7071 46.7071ZM0.142781 3.69014C0.185558 4.24077 0.666606 4.65246 1.21723 4.60968C1.76786 4.56691 2.17955 4.08586 2.13677 3.53523L0.142781 3.69014ZM3.23382 10.5104C3.10531 9.97332 2.56571 9.64207 2.02858 9.77058C1.49146 9.89909 1.16021 10.4387 1.28872 10.9758L3.23382 10.5104ZM3.56678 17.9913C3.77833 18.5015 4.3634 18.7435 4.87356 18.532C5.38372 18.3204 5.62578 17.7353 5.41423 17.2252L3.56678 17.9913ZM8.62477 23.5144C8.33577 23.0438 7.71996 22.8966 7.24933 23.1856C6.7787 23.4746 6.63146 24.0904 6.92047 24.561L8.62477 23.5144ZM11.2604 30.5247C11.6192 30.9445 12.2505 30.9939 12.6703 30.635C13.0901 30.2762 13.1395 29.6449 12.7806 29.2251L11.2604 30.5247ZM17.7748 34.2194C17.3551 33.8605 16.7238 33.9099 16.3649 34.3297C16.0061 34.7495 16.0555 35.3807 16.4752 35.7396L17.7748 34.2194ZM22.4389 40.0795C22.9096 40.3685 23.5254 40.2213 23.8144 39.7507C24.1034 39.28 23.9562 38.6642 23.4855 38.3752L22.4389 40.0795ZM29.7748 41.5858C29.2646 41.3742 28.6796 41.6163 28.468 42.1264C28.2565 42.6366 28.4985 43.2217 29.0087 43.4332L29.7748 41.5858ZM36.0242 45.7113C36.5613 45.8398 37.1009 45.5085 37.2294 44.9714C37.3579 44.4343 37.0267 43.8947 36.4895 43.7662L36.0242 45.7113ZM43.4648 44.8632C42.9141 44.8204 42.4331 45.2321 42.3903 45.7828C42.3475 46.3334 42.7592 46.8144 43.3099 46.8572L43.4648 44.8632ZM0 0C0 1.2417 0.0481734 2.47234 0.142781 3.69014L2.13677 3.53523C2.04616 2.36892 2 1.18995 2 0H0ZM1.28872 10.9758C1.86684 13.3922 2.63156 15.736 3.56678 17.9913L5.41423 17.2252C4.51908 15.0666 3.78716 12.8232 3.23382 10.5104L1.28872 10.9758ZM6.92047 24.561C8.212 26.6642 9.66436 28.6578 11.2604 30.5247L12.7806 29.2251C11.2521 27.4372 9.86137 25.5282 8.62477 23.5144L6.92047 24.561ZM16.4752 35.7396C18.3422 37.3356 20.3358 38.788 22.4389 40.0795L23.4855 38.3752C21.4718 37.1386 19.5628 35.7479 17.7748 34.2194L16.4752 35.7396ZM29.0087 43.4332C31.2639 44.3684 33.6078 45.1332 36.0242 45.7113L36.4895 43.7662C34.1768 43.2128 31.9334 42.4809 29.7748 41.5858L29.0087 43.4332ZM43.3099 46.8572C44.5277 46.9518 45.7583 47 47 47V45C45.8101 45 44.6311 44.9538 43.4648 44.8632L43.3099 46.8572Z"
                    fill="var(--dark-3)"
                  />
                </svg>

                <svg
                  width="841"
                  height="46"
                  viewBox="0 0 841 46"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ position: "absolute", left: 66, top: 66 }}
                >
                  <g opacity="0.5">
                    <rect x="28" y="1" width="188" height="44" fill="var(--dark-0)" stroke="var(--dark-3)" />
                    <rect x="40" y="16" width="86" height="15" rx="4" fill="var(--dark-3)" />
                    <rect x="216" y="1" width="196" height="44" fill="var(--dark-0)" stroke="var(--dark-3)" />
                    <rect x="456" y="1" width="180" height="44" fill="var(--dark-0)" stroke="var(--dark-3)" />
                    <rect x="636" y="1" width="68" height="44" fill="var(--dark-0)" stroke="var(--dark-3)" />
                    <rect x="412" y="1" width="44" height="44" fill="var(--dark-0)" stroke="var(--dark-3)" />
                    <rect x="704" y="1" width="68" height="44" fill="var(--dark-0)" stroke="var(--dark-3)" />
                    <rect x="772" y="1" width="68" height="44" fill="var(--dark-0)" stroke="var(--dark-3)" />
                    <path
                      d="M18.1001 33.1001H5.6001C4.49593 33.1001 3.6001 32.2043 3.6001 31.1001V18.6001C3.6001 17.4959 4.49593 16.6001 5.6001 16.6001H18.1001C19.2043 16.6001 20.1001 17.4959 20.1001 18.6001V31.1001C20.1001 32.2043 19.2043 33.1001 18.1001 33.1001Z"
                      stroke="var(--dark-3)"
                      strokeWidth="1.2"
                      strokeMiterlimit="10"
                    />
                    <path
                      d="M616 23.8701L618.714 21.2157C619.008 20.9281 619.485 20.9281 619.779 21.2157C620.074 21.5033 620.074 21.9701 619.779 22.2577L616.502 25.463C616.224 25.7346 615.775 25.7346 615.498 25.463L612.221 22.2577C611.926 21.9701 611.926 21.5033 612.221 21.2157C612.515 20.9281 612.992 20.9281 613.286 21.2157L616 23.8701Z"
                      fill="var(--dark-5)"
                    />
                    <rect x="232" y="16" width="86" height="15" rx="4" fill="var(--dark-3)" />
                    <rect x="472" y="16" width="86" height="15" rx="4" fill="var(--dark-3)" />
                    <rect x="654" y="16" width="32" height="15" rx="4" fill="var(--dark-3)" />
                    <rect x="722" y="16" width="32" height="15" rx="4" fill="var(--dark-3)" />
                    <rect x="790" y="16" width="32" height="15" rx="4" fill="var(--dark-3)" />
                    <rect x="427" y="16" width="15" height="15" rx="4" fill="var(--dark-3)" />
                  </g>
                </svg>
                <KuikaButton
                  onClick={() => this.onClickAddSection()}
                  style={{
                    marginTop: 90,
                    marginLeft: "auto",
                    marginRight: "auto",
                    width: "129px !important",
                    height: "30px !important"
                  }}
                  disabled={
                    this.isEditMode &&
                    Boolean(this.props.metadata?.linkedTable?.tableName) &&
                    this.props.metadata?.linkedTable?.tableName !== "" &&
                    (this.state.isTablesLoading || this.state.isSyncLoading) &&
                    this.getIsSyncedTableNotFound()
                  }
                >
                  Add Section
                </KuikaButton>
              </div>
            )}
          </Droppable>
        </div>
        <DefaultModal
          showModal={this.state.isEmptySectionWarningModalVisible}
          firstHeader="Warning"
          width={460}
          dontRenderSpinner
          closeModal={this.onClickCancelOnEmptySectionWarningModal}
          footer={
            <KModalFooter
              submitLabel="Continue"
              onSubmit={this.onClickContinueOnEmptySectionWarningModal}
              onCancel={this.onClickCancelOnEmptySectionWarningModal}
              disabled={this.getIsCreateButtonDisabled()}
              loading={this.getIsNextButtonLoading()}
            />
          }
        >
          <div className="form-wizard-warning">
            {this.getEmptySections().length === 1 && (
              <div>
                Section <span style={{ fontWeight: 700 }}>"{this.getSectionTitle(this.getEmptySections()[0])}"</span>{" "}
                has no items. If you choose to continue, this section will not be displayed in the form.
              </div>
            )}
            {this.getEmptySections().length > 1 && (
              <div>
                Sections{" "}
                <span style={{ fontWeight: 700 }}>
                  {this.getEmptySections()
                    .map((section: IFormSection) => `"${section.title}"`)
                    .join(", ")}
                </span>{" "}
                have no items. If you choose to continue, these sections will not be displayed in the form.
              </div>
            )}
          </div>
        </DefaultModal>
        <DefaultModal
          showModal={this.state.isUnlinkWarningModalOpen}
          firstHeader="Warning"
          width={460}
          dontRenderSpinner
          closeModal={this.onClickCancelUnlinkWarningModal}
          footer={
            <KModalFooter
              submitLabel="Continue"
              onSubmit={this.onClickUnlink}
              onCancel={this.onClickCancelUnlinkWarningModal}
              loading={this.state.isUnlinkLoading}
            />
          }
        >
          <div className="form-wizard-warning">
            <div>
              Unlinking the table will cause the form to lose its connection to the table. The form will no longer be
              synced with the table.
            </div>
          </div>
        </DefaultModal>
      </DragDropContext>
    );
  }
}

const formWizardCustom = withSD<IFormWizardCustomProps>(FormWizardCustom);
export { formWizardCustom as FormWizardCustom };
